// Webhook subscriptions admin page (v101).
//
// v185 — event picker now consumes the GET /api/admin/webhooks/events
// catalogue, rendering a grouped checkbox list with human-readable
// labels + descriptions instead of raw dot-separated keys. Wire-format
// keys are unchanged, so existing subscriptions keep working.

import { useMemo, useState } from 'react';
import { Plus, Trash2, Webhook, MessageSquare, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { AdminShell } from '../../components/AdminShell';
import { Card, CardBody, CardFooter, CardHeader } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import {
  useWebhooks,
  useWebhookDeliveries,
  useEventCatalogue,
  createWebhook,
  deleteWebhook,
  type EventDescriptor,
} from '../../api/webhooks';

const selectClass =
  'h-10 px-3 rounded-md bg-bg-inset text-fg border border-border focus:border-accent focus:bg-bg-elevated transition-colors text-sm';

function deliveryStatusTone(status: 'pending' | 'sent' | 'failed') {
  if (status === 'sent') return 'success';
  if (status === 'failed') return 'danger';
  return 'warning';
}

function DeliveryStatusIcon({ status }: { status: 'pending' | 'sent' | 'failed' }) {
  if (status === 'sent') return <CheckCircle2 className="h-3 w-3" />;
  if (status === 'failed') return <XCircle className="h-3 w-3" />;
  return <Clock className="h-3 w-3" />;
}

// Fallback used when the catalogue endpoint hasn't responded yet
// (network blip, brand-new deployment) — keeps the picker usable
// without flashing a blank list. Wire keys are the canonical ones.
const FALLBACK_EVENTS: EventDescriptor[] = [
  { key: 'block.created', category: 'Security', label: 'block.created', description: '' },
  { key: 'block.hit', category: 'Security', label: 'block.hit', description: '' },
  { key: 'artifact.uploaded', category: 'Lifecycle', label: 'artifact.uploaded', description: '' },
  { key: 'advisory.match', category: 'Detection', label: 'advisory.match', description: '' },
  { key: 'promotion.requested', category: 'Lifecycle', label: 'promotion.requested', description: '' },
  { key: 'promotion.approved', category: 'Lifecycle', label: 'promotion.approved', description: '' },
];

// Backward-compat wrapper — see AdminNotificationsHub for the
// consolidated IA. App.tsx redirects /admin/webhooks to
// /admin/notifications?tab=webhooks; this export remains for any
// direct importers.
export function AdminWebhooks() {
  return (
    <AdminShell
      title="Webhooks"
      description="POST events to an HTTP endpoint or Slack incoming webhook. The HMAC-SHA256 secret signs the body in the X-OrbitalReg-Signature header — Slack targets get a pretty-printed message instead."
    >
      <AdminWebhooksBody />
    </AdminShell>
  );
}

// Body for the Notifications hub — no <AdminShell> wrapper.
export function AdminWebhooksBody() {
  const { data: subs, mutate } = useWebhooks();
  const { data: deliveries } = useWebhookDeliveries(30);
  const { data: catalogue } = useEventCatalogue();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [type, setType] = useState<'http' | 'slack'>('http');
  const [secret, setSecret] = useState('');
  const [evts, setEvts] = useState<string[]>(['block.created']);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // Flatten the catalogue into (a) groups for rendering and (b) a
  // key→descriptor map so the subscriptions table can show friendly
  // labels next to the wire-format key.
  const groups = useMemo(() => {
    if (catalogue?.categories?.length) return catalogue.categories;
    return [{ category: 'Events', events: FALLBACK_EVENTS }];
  }, [catalogue]);

  const labels = useMemo(() => {
    const m = new Map<string, EventDescriptor>();
    for (const g of groups) for (const e of g.events) m.set(e.key, e);
    return m;
  }, [groups]);

  const onCreate = async () => {
    setBusy(true);
    setErr(null);
    try {
      await createWebhook({
        name, target_url: url, target_type: type,
        secret: secret || undefined, events: evts, active: true,
      });
      setName(''); setUrl(''); setSecret(''); setEvts(['block.created']);
      await mutate();
    } catch (e) {
      setErr(String(e));
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async (id: string) => {
    if (!confirm('Delete this webhook subscription?')) return;
    await deleteWebhook(id);
    await mutate();
  };

  const toggleEvt = (e: string) => {
    setEvts((cur) => (cur.includes(e) ? cur.filter((x) => x !== e) : [...cur, e]));
  };

  return (
    <>
      <Card>
        <CardHeader title="New subscription" />
        <CardBody className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="name (e.g. ops-slack)" />
            <select
              className={selectClass + ' w-full'}
              value={type}
              onChange={(e) => setType(e.target.value as 'http' | 'slack')}
            >
              <option value="http">Generic HTTP webhook</option>
              <option value="slack">Slack incoming webhook</option>
            </select>
          </div>
          <Input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://hooks.example.com/orbitalreg" />
          {type === 'http' && (
            <Input
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder="HMAC secret (optional, recommended)"
            />
          )}
          <div>
            <p className="orb-section-title text-fg-muted mb-2">Events</p>
            <p className="text-fg-muted text-xs mb-3">
              Pick which events POST to this target. Wire-format keys stay stable;
              the labels here are just the operator-facing name.
            </p>
            <div className="space-y-4">
              {groups.map((g) => (
                <div key={g.category}>
                  <p className="text-xs uppercase tracking-wider text-fg-muted mb-2">
                    {g.category}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {g.events.map((ev) => {
                      const checked = evts.includes(ev.key);
                      return (
                        <label
                          key={ev.key}
                          className={
                            'flex items-start gap-3 p-3 rounded-md border cursor-pointer transition-colors ' +
                            (checked
                              ? 'border-nebula bg-nebula/10'
                              : 'border-border hover:border-fg-muted/40 hover:bg-bg-hover/40')
                          }
                        >
                          <input
                            type="checkbox"
                            className="mt-1 h-4 w-4 rounded accent-nebula"
                            checked={checked}
                            onChange={() => toggleEvt(ev.key)}
                          />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-fg">
                              {ev.label}
                            </span>
                            {ev.description && (
                              <span className="block text-xs text-fg-muted mt-0.5">
                                {ev.description}
                              </span>
                            )}
                            <span className="block text-[10px] font-mono text-fg-muted/70 mt-1">
                              {ev.key}
                            </span>
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
          {err && <p className="text-danger font-mono text-sm">{err}</p>}
        </CardBody>
        <CardFooter className="flex justify-end">
          <Button
            variant="primary"
            disabled={busy || !name || !url || evts.length === 0}
            onClick={onCreate}
          >
            <Plus className="h-4 w-4" />
            Create subscription
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader title={`Subscriptions (${subs?.length ?? 0})`} />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Name</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Type</th>
                <th className="px-5 py-3 font-medium text-fg-muted">URL</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Events</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Last fired</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {subs?.length ? (
                subs.map((s) => (
                  <tr key={s.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                    <td className="px-5 py-3 text-fg flex items-center gap-2">
                      {s.target_type === 'slack' ? <MessageSquare className="h-3.5 w-3.5" /> : <Webhook className="h-3.5 w-3.5" />}
                      {s.name}
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">{s.target_type}</td>
                    <td className="px-5 py-3 text-fg-muted text-xs font-mono break-all max-w-xs">{s.target_url}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1">
                        {s.events.map((e) => {
                          const d = labels.get(e);
                          return (
                            <Badge key={e} tone="neutral" title={d?.description || e}>
                              {d?.label ?? e}
                            </Badge>
                          );
                        })}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-fg-muted text-xs">
                      {s.last_fired_at ? new Date(s.last_fired_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-5 py-3">
                      <button
                        type="button"
                        onClick={() => onDelete(s.id)}
                        className="h-8 w-8 inline-flex items-center justify-center rounded-md text-fg-muted hover:bg-danger/10 hover:text-danger"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No subscriptions yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <CardHeader title="Recent deliveries" description="Last 30 attempts. Auto-refreshed every 15s." />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th className="px-5 py-3 font-medium text-fg-muted">Status</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Event</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Target</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Created</th>
                <th className="px-5 py-3 font-medium text-fg-muted">Code</th>
              </tr>
            </thead>
            <tbody>
              {deliveries?.length ? (
                deliveries.map((d) => {
                  const desc = labels.get(d.event);
                  return (
                    <tr key={d.id} className="border-b border-border/60 last:border-0 hover:bg-bg-hover/40">
                      <td className="px-5 py-3">
                        <Badge tone={deliveryStatusTone(d.status)}>
                          <DeliveryStatusIcon status={d.status} />
                          {d.status}
                        </Badge>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-fg text-xs">{desc?.label ?? d.event}</span>
                        <span className="block text-[10px] font-mono text-fg-muted/70">
                          {d.event}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-fg-muted text-xs font-mono break-all max-w-xs">{d.target_url}</td>
                      <td className="px-5 py-3 text-fg-muted text-xs">
                        {new Date(d.created_at).toLocaleString()}
                      </td>
                      <td className="px-5 py-3 text-fg-muted text-xs">
                        {d.response_code ?? '—'}
                        {d.last_error && (
                          <span className="block text-danger text-[10px] truncate max-w-[200px]" title={d.last_error}>
                            {d.last_error}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={5} className="px-5 py-12 text-center text-fg-muted text-sm">
                    No deliveries yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </>
  );
}
